import type { Quiz } from '../types'

const T = { label: 'True' }
const F = { label: 'False' }

export const MORE_B: Record<string, Quiz[]> = {
  decorator: [
    {
      format: 'judge',
      prompt: 'This is a Decorator.',
      code: `type Gzip struct{ next Store }

// wraps any Store
func (g Gzip) Put(k, d string) error {
	return g.next.Put(k, zip(d))
}`,
      choices: [{ ...T, correct: true }, F],
      explanation: 'Same Store interface, adds compression, then hands the call on to the wrapped store.',
    },
    {
      format: 'judge',
      prompt: 'This is a Decorator.',
      code: `class CachedLoggedUserRepo
    extends UserRepo
{
    // also: CachedUserRepo,
    // LoggedUserRepo, RetryUserRepo,
    // RetryLoggedUserRepo ...
}`,
      choices: [T, { ...F, correct: true }],
      explanation: 'One subclass per mix is the pile of classes a Decorator replaces; it wraps nothing.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which pattern? Add logging, then caching, around a service in any mix.',
      choices: [
        { label: 'Proxy' },
        { label: 'Decorator', correct: true },
        { label: 'Adapter' },
        { label: 'Facade' },
      ],
      explanation: 'Stackable wrappers that add behaviour, with no subclass per combination: Decorator.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which problem does Decorator solve?',
      choices: [
        { label: 'Two interfaces that do not fit' },
        { label: 'A costly object built too early' },
        { label: 'Too many calls to a subsystem' },
        { label: 'Extras piling up as subclasses', correct: true },
      ],
      explanation: 'Extras become wrappers you stack at runtime instead of one subclass per combination.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `type Retry struct{ next Sender }

func (r Retry) Send(m Msg) error {
	var err error
	for i := 0; i < 3; i++ {
		if err = r.next.Send(m); err == nil {
			return nil
		}
	}
	return err
}`,
      choices: [
        { label: 'Proxy' },
        { label: 'Decorator', correct: true },
        { label: 'None of these' },
        { label: 'Misused' },
      ],
      explanation: 'It keeps the Sender interface and adds retrying around the wrapped sender.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `class Price
{
    public function __construct(
        private float $base,
    ) {}

    public function withTax(): float
    {
        return $this->base * 1.2;
    }
}`,
      choices: [
        { label: 'Decorator' },
        { label: 'Adapter' },
        { label: 'None of these', correct: true },
        { label: 'Misused' },
      ],
      explanation: 'It wraps no other object and shares no interface; it is just a value with a method.',
    },
  ],
  facade: [
    {
      format: 'judge',
      prompt: 'This is the Facade pattern.',
      code: `use Illuminate\\Support\\Facades\\Cache;

// Laravel's Cache facade
Cache::put('k', $v, 60);
$x = Cache::get('k');
Cache::forget('k');`,
      choices: [T, { ...F, correct: true }],
      explanation: 'Laravel Facades are static access to a container service, not a simplified front door.',
    },
    {
      format: 'judge',
      prompt: 'This is a Facade.',
      code: `func (s *Site) Publish(p Post) error {
	if err := s.lint.Check(p); err != nil {
		return err
	}
	s.store.Save(p)
	return s.feed.Rebuild()
}`,
      choices: [{ ...T, correct: true }, F],
      explanation: 'One call runs three parts in the right order, so callers need not know them.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which pattern? One entry point hides five classes and their call order.',
      choices: [
        { label: 'Adapter' },
        { label: 'Mediator' },
        { label: 'Facade', correct: true },
        { label: 'Proxy' },
      ],
      explanation: 'A simple front door over a wide subsystem is a Facade.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which problem does Facade solve?',
      choices: [
        { label: 'Callers repeat one long sequence', correct: true },
        { label: 'Objects use too much memory' },
        { label: 'Requests need to be undone' },
        { label: 'Items must be walked one by one' },
      ],
      explanation: 'The sequence lives in one place instead of being copied into every caller.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `type Mailer struct {
	tpl  *Templates
	smtp *SMTP
}

func (m *Mailer) Welcome(to string) error {
	body := m.tpl.Render("welcome", to)
	return m.smtp.Send(to, body)
}`,
      choices: [
        { label: 'Mediator' },
        { label: 'None of these' },
        { label: 'Misused' },
        { label: 'Facade', correct: true },
      ],
      explanation: 'Welcome() hides template and SMTP details behind one plain call.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `class StripeGateway implements Gateway
{
    public function pay(int $cents): bool
    {
        return $this->sdk
            ->charges
            ->create(['amount' => $cents])
            ->paid;
    }
}`,
      choices: [
        { label: 'Facade' },
        { label: 'Adapter', correct: true },
        { label: 'None of these' },
        { label: 'Misused' },
      ],
      explanation: 'It makes a vendor SDK fit our Gateway interface: an Adapter, not a simplifier.',
    },
  ],
  flyweight: [
    {
      format: 'judge',
      prompt: 'This is a Flyweight.',
      code: `class IconPool
{
    private static array $icons = [];

    public static function get(string $n)
    {
        return self::$icons[$n]
            ??= new Icon($n);
    }
}`,
      choices: [{ ...T, correct: true }, F],
      explanation: 'One Icon per name is built once and shared with every caller who asks for it.',
    },
    {
      format: 'judge',
      prompt: 'This shared Style is a safe Flyweight.',
      code: `var shared = &Style{Font: "arial"}

func Bold(s *Style) { s.Bold = true }

func main() {
	a, b := shared, shared
	Bold(a) // b turns bold too
	_ = b
}`,
      choices: [T, { ...F, correct: true }],
      explanation: 'A shared object must be immutable; here one caller changes what everyone sees.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which pattern? A million trees share one mesh and texture to save memory.',
      choices: [
        { label: 'Prototype' },
        { label: 'Flyweight', correct: true },
        { label: 'Singleton' },
        { label: 'Proxy' },
      ],
      explanation: 'Sharing the repeated, unchanging data between many objects is a Flyweight.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which problem does Flyweight solve?',
      choices: [
        { label: 'Callers repeat one long sequence' },
        { label: 'A request must be undone' },
        { label: 'Repeated data eats memory', correct: true },
        { label: 'Two interfaces do not fit' },
      ],
      explanation: 'Many objects hold identical heavy data; sharing one copy cuts the memory use.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `var cache = map[string]*Style{}

func StyleFor(name string) *Style {
	s := &Style{Name: name}
	cache[name] = s
	return s
}`,
      choices: [
        { label: 'Flyweight' },
        { label: 'Singleton' },
        { label: 'None of these' },
        { label: 'Misused', correct: true },
      ],
      explanation: 'The cache is written but never read, so each call still makes a new Style.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `class Config
{
    private static ?self $me = null;

    public static function get(): self
    {
        return self::$me ??= new self();
    }
}`,
      choices: [
        { label: 'Flyweight' },
        { label: 'Singleton', correct: true },
        { label: 'None of these' },
        { label: 'Misused' },
      ],
      explanation: 'Exactly one instance for the whole app, not one shared object per distinct value.',
    },
  ],
  proxy: [
    {
      format: 'judge',
      prompt: 'This is a Proxy.',
      code: `type CachedGeo struct {
	real  Geo
	cache map[string]Pos
}

func (c *CachedGeo) Find(q string) Pos {
	if p, ok := c.cache[q]; ok {
		return p
	}
	p := c.real.Find(q)
	c.cache[q] = p
	return p
}`,
      choices: [{ ...T, correct: true }, F],
      explanation: 'It stands in for Geo and decides whether the real call is needed: a caching Proxy.',
    },
    {
      format: 'judge',
      prompt: 'This is a Proxy.',
      code: `class ReportFactory
{
    public function make(): Report
    {
        return new HeavyReport();
    }
}`,
      choices: [T, { ...F, correct: true }],
      explanation: 'It just creates objects. It is not a stand-in with the real interface, so no Proxy.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which pattern? A local object stands in for a remote service.',
      choices: [
        { label: 'Adapter' },
        { label: 'Facade' },
        { label: 'Decorator' },
        { label: 'Proxy', correct: true },
      ],
      explanation: 'A stand-in with the real interface that hides the network hop is a remote Proxy.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which problem does Proxy solve?',
      choices: [
        { label: 'Stacking extras around an object' },
        { label: 'Control access to a costly object', correct: true },
        { label: 'Making two interfaces fit' },
        { label: 'Undoing a past request' },
      ],
      explanation: 'It decides whether, when and how the call reaches the real object.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `type RemoteUsers struct{ c *http.Client }

func (r RemoteUsers) Get(i int) (U, error) {
	resp, err := r.c.Get(urlFor(i))
	if err != nil {
		return U{}, err
	}
	return decode(resp)
}`,
      choices: [
        { label: 'Adapter' },
        { label: 'None of these' },
        { label: 'Proxy', correct: true },
        { label: 'Misused' },
      ],
      explanation: 'It stands in for a remote service behind the same Users interface.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `class LazyReport implements Report
{
    private Report $real;

    public function __construct()
    {
        $this->real = new HeavyReport();
    }
    public function render(): string
    { return $this->real->render(); }
}`,
      choices: [
        { label: 'Proxy' },
        { label: 'Decorator' },
        { label: 'None of these' },
        { label: 'Misused', correct: true },
      ],
      explanation: 'Called lazy, but it builds the heavy report in the constructor, so nothing is delayed.',
    },
  ],
  'chain-of-responsibility': [
    {
      format: 'judge',
      prompt: 'This is a link in a Chain of Responsibility.',
      code: `func Auth(next Handler) Handler {
	return func(r Req) {
		if r.Token == "" {
			return
		}
		next(r)
	}
}`,
      choices: [{ ...T, correct: true }, F],
      explanation: 'It either stops the request or hands it to next, like any middleware.',
    },
    {
      format: 'judge',
      prompt: 'This is a Chain of Responsibility.',
      code: `if ($type === 'a') {
    handleA($req);
} elseif ($type === 'b') {
    handleB($req);
} else {
    handleC($req);
}`,
      choices: [T, { ...F, correct: true }],
      explanation: 'One place picks the handler by type; no handler passes the request to the next.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which pattern? Steps see a request in turn; any may stop it or send it on.',
      choices: [
        { label: 'Observer' },
        { label: 'Mediator' },
        { label: 'Command' },
        { label: 'Chain of Responsibility', correct: true },
      ],
      explanation: 'A request handed along a line of handlers, each free to stop it, is a Chain.',
    },
    {
      format: 'quick_pick',
      prompt: 'Odd one out: which is not a chain?',
      choices: [
        { label: 'Laravel middleware' },
        { label: 'A filter pipeline with next()' },
        { label: 'A queued job class', correct: true },
        { label: 'Handlers that may pass it on' },
      ],
      explanation: 'A queued job is a request kept as an object (Command); the others pass a request along.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `class RateLimit extends Link
{
    public function handle(Req $r)
    {
        if ($this->tooMany($r)) {
            return response('slow', 429);
        }
        return parent::handle($r);
    }
}`,
      choices: [
        { label: 'Decorator' },
        { label: 'None of these' },
        { label: 'Chain of Responsibility', correct: true },
        { label: 'Misused' },
      ],
      explanation: 'It may end the request with a 429, otherwise it passes it to the next link.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `type Bus struct{ subs []func(Event) }

func (b *Bus) Emit(e Event) {
	for _, s := range b.subs {
		s(e)
	}
}`,
      choices: [
        { label: 'Chain of Responsibility' },
        { label: 'Observer', correct: true },
        { label: 'None of these' },
        { label: 'Misused' },
      ],
      explanation: 'Every subscriber is told about the event; none can stop it, so it is Observer.',
    },
  ],
  command: [
    {
      format: 'judge',
      prompt: 'This is a Command.',
      code: `class DeleteUser
{
    public function __construct(
        public int $id,
    ) {}

    public function run(): void
    {
        User::destroy($this->id);
    }
}`,
      choices: [{ ...T, correct: true }, F],
      explanation: 'The request is an object holding its data and one method to run it later.',
    },
    {
      format: 'judge',
      prompt: 'This job is safe to put on a queue.',
      code: `class ExportJob implements ShouldQueue
{
    public function __construct(
        public PDO $db,
        public $file,
    ) {}
}`,
      choices: [T, { ...F, correct: true }],
      explanation: 'A live connection and open file cannot travel to a queue; carry ids instead.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which pattern? A request becomes an object to queue, log or undo.',
      choices: [
        { label: 'Strategy' },
        { label: 'Command', correct: true },
        { label: 'Memento' },
        { label: 'Observer' },
      ],
      explanation: 'Wrapping a request as an object so it can be kept and replayed is a Command.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which problem does Command solve?',
      choices: [
        { label: 'A heavy object is built too early' },
        { label: 'Many classes need one entry point' },
        { label: 'A collection hides its layout' },
        { label: 'A request must be stored or undone', correct: true },
      ],
      explanation: 'Once the request is an object, it can be stored, retried or reversed.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `type Cmd interface {
	Do()
	Undo()
}

type AddLine struct{ doc *Doc; text string }

func (a AddLine) Do() { a.doc.Add(a.text) }
func (a AddLine) Undo() { a.doc.Pop() }`,
      choices: [
        { label: 'Memento' },
        { label: 'None of these' },
        { label: 'Command', correct: true },
        { label: 'Misused' },
      ],
      explanation: 'Each action is an object with its data, and it knows how to run and reverse itself.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `class Cart
{
    public function __construct(
        private Pricing $pricing,
    ) {}

    public function total(int $n): int
    {
        return $this->pricing->cost($n);
    }
}`,
      choices: [
        { label: 'Command' },
        { label: 'Strategy', correct: true },
        { label: 'None of these' },
        { label: 'Misused' },
      ],
      explanation: 'A swappable Pricing is called right away; nothing is stored or queued, so Strategy.',
    },
  ],
  iterator: [
    {
      format: 'judge',
      prompt: 'This is an Iterator.',
      code: `function rows(string $path): Generator
{
    $f = fopen($path, 'r');
    while (($l = fgets($f)) !== false) {
        yield $l;
    }
    fclose($f);
}`,
      choices: [{ ...T, correct: true }, F],
      explanation: 'Callers get one line at a time and never see how the file is read.',
    },
    {
      format: 'judge',
      prompt: 'This is an Iterator.',
      code: `// the slice is the storage itself
func (l *List) Items() []Item {
	return l.items
}
// caller: for _, it := range l.Items()
// caller can also append to it`,
      choices: [T, { ...F, correct: true }],
      explanation: 'It hands out the raw storage; an Iterator steps through items and hides the layout.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which pattern? One loop walks a tree, a list or a DB cursor.',
      choices: [
        { label: 'Composite' },
        { label: 'Visitor' },
        { label: 'Observer' },
        { label: 'Iterator', correct: true },
      ],
      explanation: 'A uniform way to step through a collection without knowing its layout: Iterator.',
    },
    {
      format: 'quick_pick',
      prompt: 'Which problem does Iterator solve?',
      choices: [
        { label: 'Guarding a remote service' },
        { label: 'Undoing past requests' },
        { label: 'Looping without knowing the layout', correct: true },
        { label: 'Sharing duplicates to save memory' },
      ],
      explanation: 'Callers loop the same way whether the data is an array, a list or a cursor.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `type Cursor struct {
	rows []Row
	i    int
}

func (c *Cursor) Next() bool {
	c.i++
	return c.i <= len(c.rows)
}
func (c *Cursor) Row() Row {
	return c.rows[c.i-1]
}`,
      choices: [
        { label: 'Iterator', correct: true },
        { label: 'Observer' },
        { label: 'None of these' },
        { label: 'Misused' },
      ],
      explanation: 'The cursor holds the position; Next() and Row() step through without exposing rows.',
    },
    {
      format: 'spot',
      prompt: 'Which pattern is this?',
      code: `class Bag implements IteratorAggregate
{
    private array $items = [];

    public function getIterator(): Iterator
    {
        $this->items = [];
        return new ArrayIterator(
            $this->items,
        );
    }
}`,
      choices: [
        { label: 'Iterator' },
        { label: 'Composite' },
        { label: 'None of these' },
        { label: 'Misused', correct: true },
      ],
      explanation: 'Asking for an iterator wipes the bag; walking a collection must never change it.',
    },
  ],
}
